// src/pages/Auditorias/index.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ehAdmin } from '../../utils/permissoes';
import { useAuditorias } from './hooks/useAuditorias';
import AuditoriaFiltros from './components/AuditoriaFiltros';
import AuditoriasTable from './components/AuditoriasTable';
import NovaAuditoriaDialog from './components/NovaAuditoriaDialog';
import ProcessosAlteradosDialog from './components/ProcessosAlteradosDialog';
import ExcluirAuditoriaDialog from './components/ExcluirAuditoriaDialog';
import ParecerDialog from './components/ParecerDialog';
import './Auditorias.css';

/**
 * Gestão geral das auditorias de processos. Ecrã de administração.
 *
 * O acesso é barrado no servidor (requireAdmin); aqui o item de menu só aparece
 * a administradores.
 */
const Auditorias = () => {
  const { user } = useAuth();
  const admin = ehAdmin(user);
  const {
    auditorias, advogados, carregando, erro,
    filtros, actualizarFiltro, disparar, remover, haTrabalho, recarregar
  } = useAuditorias();

  const [novaAberta, setNovaAberta] = useState(false);
  const [alteradosAberto, setAlteradosAberto] = useState(false);
  const [aExcluir, setAExcluir] = useState(null);
  const [parecerDe, setParecerDe] = useState(null);
  const [aviso, setAviso] = useState(null);

  const atualizarProcesso = async (auditoria) => {
    setAviso(null);
    try {
      await disparar({
        cod_cli: auditoria.cod_cli,
        cod_processo: auditoria.cod_processo,
        user_email: auditoria.user_email,
        modo: 'atualizacao',
      });
      setAviso(`Atualização de ${auditoria.cod_processo} enfileirada.`);
    } catch (e) {
      setAviso(
        e.response?.status === 409
          ? `${auditoria.cod_processo} já tem uma execução na fila.`
          : e.response?.data?.message || 'Erro ao enfileirar a atualização'
      );
    }
  };

  // Number() defensivo: contagens agregadas do Postgres chegam como string se
  // alguma query esquecer o ::int, e a soma passaria a concatenação
  const totais = auditorias.reduce((acc, a) => ({
    graves: acc.graves + Number(a.graves || 0),
    porValidar: acc.porValidar + (a.validacao === 'pendente' && a.estado === 'concluida' ? 1 : 0),
    emCurso: acc.emCurso + (a.estado === 'em_execucao' || a.estado === 'pendente' ? 1 : 0),
  }), { graves: 0, porValidar: 0, emCurso: 0 });

  return (
    <div className="aud-pagina">
      <div className="aud-cabecalho">
        <div>
          <h1>Auditoria de processos</h1>
          <p className="aud-sub">
            Cronologia, conferência peça a peça e parecer final, por cliente-processo.
          </p>
        </div>
        <div className="aud-cabecalho-accoes">
          {/* Os prompts do motor valem para as auditorias de toda a gente */}
          {admin && (
            <Link
              to="/auditorias/configuracoes"
              className="aud-engrenagem"
              title="Configurações dos prompts"
              aria-label="Configurações dos prompts"
            >
              ⚙
            </Link>
          )}
          <button type="button" className="aud-btn-secundario" onClick={recarregar}>
            ACTUALIZAR
          </button>
          <button type="button" className="aud-btn-primario" onClick={() => setNovaAberta(true)}>
            NOVA AUDITORIA
          </button>
          <button
            type="button"
            className="aud-btn-secundario"
            onClick={() => { setAviso(null); setAlteradosAberto(true); }}
          >
            ATUALIZAÇÕES DE PROCESSOS
          </button>
        </div>
      </div>

      <div className="aud-metricas">
        <div className="aud-metrica">
          <span className="aud-metrica-rotulo">Processos</span>
          <span className="aud-metrica-valor">{auditorias.length}</span>
        </div>
        <div className="aud-metrica">
          <span className="aud-metrica-rotulo">Por validar</span>
          <span className="aud-metrica-valor">{totais.porValidar}</span>
        </div>
        <div className="aud-metrica">
          <span className="aud-metrica-rotulo">Peças graves</span>
          <span className="aud-metrica-valor aud-metrica-alerta">{totais.graves}</span>
        </div>
        <div className="aud-metrica">
          <span className="aud-metrica-rotulo">Em execução</span>
          <span className="aud-metrica-valor">{totais.emCurso}</span>
        </div>
      </div>

      <AuditoriaFiltros
        filtros={filtros}
        advogados={advogados}
        onMudar={actualizarFiltro}
        mostrarAdvogados={admin}
      />

      {haTrabalho && (
        <p className="aud-nota-polling">
          Há auditorias a decorrer. Carregue em <strong>actualizar</strong> para ver o progresso.
        </p>
      )}

      {aviso && <p className="aud-aviso">{aviso}</p>}
      {erro && <p className="aud-erro">{erro}</p>}

      {carregando ? (
        <p className="aud-vazio">A carregar…</p>
      ) : (
        <AuditoriasTable
          auditorias={auditorias}
          onVerParecer={setParecerDe}
          onAtualizar={atualizarProcesso}
          onExcluir={admin ? setAExcluir : null}
        />
      )}

      {novaAberta && (
        <NovaAuditoriaDialog
          userEmail={user?.email}
          onFechar={() => setNovaAberta(false)}
          onDisparar={disparar}
        />
      )}

      {alteradosAberto && (
        <ProcessosAlteradosDialog
          userEmail={user?.email}
          onFechar={() => setAlteradosAberto(false)}
          onDisparar={disparar}
        />
      )}

      {aExcluir && (
        <ExcluirAuditoriaDialog
          auditoria={aExcluir}
          onFechar={() => setAExcluir(null)}
          onConfirmar={async (id) => {
            await remover(id);
            setAviso(`Auditoria de ${aExcluir.cod_processo} apagada.`);
          }}
        />
      )}

      {parecerDe && (
        <ParecerDialog auditoriaId={parecerDe} onClose={() => setParecerDe(null)} />
      )}
    </div>
  );
};

export default Auditorias;
